import { curry } from "lodash";
import { createSelector } from "reselect";
import { transformToHierarchyView } from "~/EasyProjectsCommon/TaskHierarchyNameHelper";
import { AppEntity } from "~/normalizr/entities";
import { BasePortfolio } from "~/normalizr/PortfoliosSchema";
import { SortUtility } from "~/utils/sort";
import { PortfolioListItem, PortfoliosRootState } from "./types";

export const portfoliosSelector = (state: PortfoliosRootState) => state.portfolios;
export const isPortfoliosDataLoaded = (state: PortfoliosRootState) => !!state.portfolios;
export const getPortfolio = curry((id: number, state: PortfoliosRootState) => state.portfolios?.[id]);

/**
 * Recursively adds level to each portfolio based on its parent's level,
 * and places it right after parent.
 * @example
 * in -> {
    undefined: [10, 20, 30]
    10: [40, 50]
    20: [60]
 * out -> [10(lvl 0), 40(lvl 1), 50(lvl 1), 20(lvl 0), 60(lvl 1), 30(lvl 0)], mapped to entities.
 */
export const addLevelToPortfolio = (
    entities: Dictionary<BasePortfolio>,
    children: Dictionary<number[]>,
    result: PortfolioListItem[],
    singleLevelPortfolios: number[],
    Level: number,
    collapsed: Dictionary<boolean> = {}
) => {
    (singleLevelPortfolios || []).forEach(id => {
        result.push({ ...entities[id], Level, IsExpandable: !!children[id] });

        if (collapsed[id]) {
            return;
        }

        addLevelToPortfolio(entities, children, result, children[id], Level + 1, collapsed);
    });

    return result;
};

export const topLevelPortfolioKey = 0;

/**
 * Groups portfolios by parent.
 * @example
 * in -> [{ Id: 10 }, { Id: 20, ParentId: 10 }]
 * out -> {
    0: [10]  // top level portfolios
    10: [20] // children of portflio id=10
 }
 */
export const groupByParentPortfolio = (result: Dictionary<number[]>, { Id, ParentId }: BasePortfolio) => {
    (result[ParentId ?? topLevelPortfolioKey] ??= []).push(Id);
    return result;
};

export const getPortfoliosHierarchy = createSelector(portfoliosSelector, entities => {
    const sortedPortfolios = Object.values(entities ?? {}).sort(SortUtility.nameComparer);

    const children = sortedPortfolios.reduce<Dictionary<number[]>>(groupByParentPortfolio, {});
    const topLevelPortfolios = children[topLevelPortfolioKey];

    return addLevelToPortfolio(entities, children, [], topLevelPortfolios, 0);
});

export const getPortfoliosOptions = createSelector(getPortfoliosHierarchy, portfolios => {
    const entities = portfolios.map(x => ({
        id: x.Id,
        label: x.Name,
        level: x.Level,
        statusName: x.PortfolioStatusName,
        EntityType: AppEntity.Portfolio,
    }));

    return transformToHierarchyView({
        entities,
        getId: item => item.id,
        getLevel: item => item.level,
        getName: item => item.label,
        combineIdAndName: false,
    });
});

export const getOptionsForParentPortfolio = createSelector(
    getPortfoliosOptions,
    (state: PortfoliosRootState, excludedPortfolioId: number) => excludedPortfolioId,
    (portfolios, excludedPortfolioId) => {
        // keeps level to exclude following children
        let parentLevel = -1;

        return portfolios.reduce((res, p) => {
            if (p.id === excludedPortfolioId) {
                parentLevel = p.level;
            } else if (parentLevel === -1 || p.level <= parentLevel) {
                // not a child
                parentLevel = -1;
                res.push(p);
            }

            return res;
        }, []);
    }
);

/**
 * @returns hash of parents by portfolio id.
 * @example
 * hierarchy -> [
    { Id: 10, Level: 0 },
    { Id: 20, ParentId: 10, Level: 1 },
    { Id: 30, ParentId: 20, Level: 2 },
]
 * result -> { 10: [], 20: [10], 30: [10, 20] }
 */
export const getPortfolioParents = createSelector(getPortfoliosHierarchy, hierarchy =>
    hierarchy.reduce<Dictionary<number[]>>((hash, { Id, ParentId }) => {
        hash[Id] = hash[ParentId]?.concat(ParentId) ?? [];
        return hash;
    }, {})
);
